import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { Add, Notifications as NotificationsIcon, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SendNotificationModal from '../components/notifications/SendNotificationModal';
import { notificationService } from '../services/api';

const Notifications = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalSent, setTotalSent] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await notificationService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined
      });

      setNotifications(response.data || []);
      setTotalCount(response.pagination?.total || 0);
      setTotalSent(response.stats?.totalSent || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.error || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    fetchNotifications();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    fetchNotifications();
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'new_video':
        return t('notifications.types.newVideo');
      case 'new_story':
        return t('notifications.types.newStory');
      case 'custom':
        return t('notifications.types.custom');
      default:
        return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'new_video':
        return 'primary';
      case 'new_story':
        return 'secondary';
      case 'custom':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('el-GR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('notifications.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setModalOpen(true)}
        >
          {t('notifications.sendNotification')}
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <NotificationsIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {totalSent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('notifications.totalSent')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            {t('notifications.history')}
          </Typography>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              size="small"
              placeholder={t('common.search')}
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit(e);
                }
              }}
            />
          </form>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box
            sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <NotificationsIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('notifications.noNotificationsFound')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('notifications.table.type')}</TableCell>
                    <TableCell>{t('notifications.table.title')}</TableCell>
                    <TableCell>{t('notifications.table.body')}</TableCell>
                    <TableCell>{t('notifications.table.recipient')}</TableCell>
                    <TableCell>{t('notifications.table.date')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map((notification) => (
                    <TableRow key={notification._id}>
                      <TableCell>
                        <Chip
                          label={getTypeLabel(notification.push_type)}
                          color={getTypeColor(notification.push_type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {notification.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {notification.body}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {notification.for_all ? (
                          <Chip label={t('notifications.allUsers')} size="small" color="success" />
                        ) : notification.receiver ? (
                          <Typography variant="body2">
                            {notification.receiver.fullName || notification.receiver.email}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('common.notAvailable')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(notification.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${t('common.of')} ${count}`
              }
            />
          </>
        )}
      </Paper>

      <SendNotificationModal
        open={modalOpen}
        onClose={handleModalClose}
      />
    </Box>
  );
};

export default Notifications;